<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head no-print">
        <div>
          <h1>Barcode labels</h1>
          <p class="lede">Print thermal-sized labels for shelf tagging or price marking.</p>
        </div>
        <div class="head-actions">
          <input v-model="search" placeholder="Search products / variants…" style="width:260px" />
          <select v-model="labelSize">
            <option value="small">Small (30 × 20 mm · 3 across)</option>
            <option value="medium">Medium (50 × 30 mm · 2 across)</option>
          </select>
          <button class="secondary" @click="selectAll">Select all shown</button>
          <button class="secondary" @click="clearSelection">Clear</button>
          <button class="primary" @click="print" :disabled="!queue.length">
            <i class="mdi mdi-printer"></i> Print {{ queue.length }} label{{ queue.length === 1 ? '' : 's' }}
          </button>
        </div>
      </div>

      <div class="card no-print">
        <table v-if="filtered.length">
          <thead>
            <tr><th style="width:36px"></th><th>Product</th><th>Variant</th><th>SKU</th><th>Barcode</th><th class="r">Price</th></tr>
          </thead>
          <tbody>
            <tr v-for="v in filtered" :key="v.variant_id" :class="{ picked: isPicked(v) }">
              <td><input type="checkbox" :checked="isPicked(v)" @change="togglePick(v)" style="width:auto" /></td>
              <td>{{ v.product_name }}</td>
              <td class="muted">{{ v.variant_name }}</td>
              <td class="muted small">{{ v.sku }}</td>
              <td class="muted small">{{ v.barcode || '—' }}</td>
              <td class="r">KES {{ format(v.sell_price) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No variants match.</p>
      </div>

      <!-- Print-only sheet -->
      <div class="sheet" :class="'size-' + labelSize">
        <div v-for="l in queue" :key="l.variant_id" class="label" :class="'size-' + labelSize">
          <div class="l-name">{{ l.product_name }}</div>
          <div class="l-variant">{{ l.variant_name }}</div>
          <div class="l-barcode">
            <div class="l-bars">|| ||| |  ||| || |  |||  || |||</div>
            <div class="l-code">{{ l.barcode || l.sku }}</div>
          </div>
          <div class="l-price">KES {{ format(l.sell_price) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'BarcodePrint',
  components: { Navigation },
  data() {
    return {
      variants: [], search: '',
      selected: {},
      labelSize: 'small',
    };
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      let rows = this.variants;
      if (q) rows = rows.filter(v =>
        (v.product_name || '').toLowerCase().includes(q) ||
        (v.variant_name || '').toLowerCase().includes(q) ||
        (v.sku || '').toLowerCase().includes(q) ||
        (v.barcode || '').toLowerCase().includes(q));
      return rows.slice(0, 300);
    },
    queue() {
      // Expand each selected variant by its quantity (default 1) — for now single label each.
      return this.filtered.filter(v => this.selected[v.variant_id])
        .flatMap(v => Array(this.selected[v.variant_id]).fill(v));
    },
  },
  async mounted() { await this.load(); },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); },
    async load() {
      // Reuse stock endpoint for the flat variant listing (has product_name + variant_name + sku + sell_price)
      const { data } = await execute({ function: 'getStockLevels' }, paysol);
      if (data.success) this.variants = data.data;
    },
    isPicked(v) { return !!this.selected[v.variant_id]; },
    togglePick(v) {
      if (this.selected[v.variant_id]) delete this.selected[v.variant_id];
      else this.selected[v.variant_id] = 1;
      this.selected = { ...this.selected };
    },
    selectAll() {
      this.filtered.forEach(v => this.selected[v.variant_id] = 1);
      this.selected = { ...this.selected };
    },
    clearSelection() { this.selected = {}; },
    print() { window.print(); },
  }
};
</script>

<style scoped>
.wrap { max-width: 1200px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; flex-wrap: wrap; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.head-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
tr.picked td { background: var(--paysol-teal-glow); }

.sheet { display: none; }
@media print {
  .no-print { display: none !important; }
  body, .wrap, .sheet { background: white !important; color: black !important; padding: 0 !important; margin: 0 !important; }
  .sheet { display: flex; flex-wrap: wrap; gap: 2mm; padding: 2mm; }
  .label {
    background: white; color: black;
    border: 1px dashed #ccc;
    display: flex; flex-direction: column; justify-content: space-between;
    font-family: 'Inter', sans-serif;
    padding: 1.5mm;
    page-break-inside: avoid;
    box-sizing: border-box;
  }
  .label.size-small  { width: 30mm; height: 20mm; font-size: 6pt; }
  .label.size-medium { width: 50mm; height: 30mm; font-size: 8pt; }
  .l-name    { font-weight: 700; font-size: 7pt; line-height: 1.1; overflow: hidden; }
  .label.size-medium .l-name { font-size: 9pt; }
  .l-variant { color: #444; font-size: 6pt; }
  .label.size-medium .l-variant { font-size: 7pt; }
  .l-bars    { font-family: 'Courier New', monospace; font-size: 12pt; letter-spacing: -1px; line-height: 1; text-align: center; }
  .label.size-medium .l-bars { font-size: 16pt; }
  .l-code    { text-align: center; font-family: 'JetBrains Mono', monospace; font-size: 6pt; }
  .label.size-medium .l-code { font-size: 7pt; }
  .l-price   { font-weight: 700; font-size: 8pt; text-align: right; }
  .label.size-medium .l-price { font-size: 11pt; }
  @page { margin: 4mm; }
}
</style>
